import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Maximize2, Minimize2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { CardSize } from '@/hooks/useDashboardSettings';

interface SortableCardProps {
    id: string;
    children: React.ReactNode;
    cardSize: CardSize;
    onResize?: (size: Partial<CardSize>) => void;
    isResizable?: boolean;
}

export function SortableCard({ id, children, cardSize, onResize, isResizable = true }: SortableCardProps) {
    // Disable resizing specifically for the map widget as requested
    const isMapWidget = id === 'activemap';
    const showResizeControls = isResizable && !isMapWidget;

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : 'auto',
        opacity: isDragging ? 0.8 : 1,
    };

    // Column span based on width
    const getColSpanClass = () => {
        switch (cardSize.width) {
            case 1: return 'col-span-1';
            case 2: return 'md:col-span-2';
            case 3: return 'md:col-span-2 lg:col-span-3';
            default: return 'col-span-1';
        }
    };

    // Height based on height setting (base 350px + 150px per step)
    // Max 3 steps as per strict requirements
    const height = 450 + (Math.min(3, Math.max(1, cardSize.height)) - 1) * 360;

    const increaseWidth = () => {
        if (cardSize.width < 3 && onResize) {
            onResize({ width: (cardSize.width + 1) as 1 | 2 | 3 });
        }
    };

    const decreaseWidth = () => {
        if (cardSize.width > 1 && onResize) {
            onResize({ width: (cardSize.width - 1) as 1 | 2 | 3 });
        }
    };

    const increaseHeight = () => {
        // Restricted max height to 3 as requested
        if (cardSize.height < 3 && onResize) {
            onResize({ height: cardSize.height + 1 });
        }
    };

    const decreaseHeight = () => {
        if (cardSize.height > 1 && onResize) {
            onResize({ height: cardSize.height - 1 });
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={{ ...style, height: `${height}px` }}
            className={cn(
                getColSpanClass(),
                'relative group/card',
                isDragging && 'ring-2 ring-primary ring-offset-2 ring-offset-background rounded-xl shadow-2xl scale-[1.02]'
            )}
        >
            {/* Control Panel - appears on hover */}
            <div className={cn(
                'absolute top-2 right-2 z-30 flex items-center gap-1',
                'opacity-0 group-hover/card:opacity-100 transition-all duration-200',
                'bg-background/90 backdrop-blur-sm border rounded-lg p-1 shadow-lg',
                isDragging && 'opacity-100'
            )}>
                {/* Resize Controls */}
                {showResizeControls && (
                    <>
                        {/* Width controls */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={decreaseWidth}
                            disabled={cardSize.width <= 1}
                            title="Zmniejsz szerokość"
                        >
                            <Minimize2 className="h-3 w-3" />
                        </Button>
                        <span className="text-xs font-mono px-1 min-w-[32px] text-center">
                            {cardSize.width}×{cardSize.height}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={increaseWidth}
                            disabled={cardSize.width >= 3}
                            title="Zwiększ szerokość"
                        >
                            <Maximize2 className="h-3 w-3" />
                        </Button>

                        <div className="w-px h-4 bg-border mx-1" />

                        {/* Height controls */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={decreaseHeight}
                            disabled={cardSize.height <= 1}
                            title="Zmniejsz wysokość"
                        >
                            <ChevronUp className="h-3 w-3" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={increaseHeight}
                            disabled={cardSize.height >= 3}
                            title="Zwiększ wysokość (+5 rekordów)"
                        >
                            <ChevronDown className="h-3 w-3" />
                        </Button>

                        <div className="w-px h-4 bg-border mx-1" />
                    </>
                )}

                {/* Drag Handle */}
                <div
                    {...attributes}
                    {...listeners}
                    className="p-1 cursor-grab active:cursor-grabbing hover:bg-muted rounded"
                    title="Przeciągnij aby zmienić kolejność"
                >
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                </div>
            </div>

            {/* Card Content */}
            <div className="h-full w-full overflow-hidden rounded-xl border bg-card text-card-foreground shadow">
                {children}
            </div>
        </div>
    );
}
